import {
  CollectionReference,
  DocumentData,
  FieldPath,
  Firestore,
  Query,
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { DBAbstract } from "./db_abstract";
import { DocumentNotFoundError } from "./document-not-found.error";
// @ts-ignore
import { collectionData, docData } from "rxfire/firestore";
import { Observable, map } from "rxjs";
import { BaseModel } from "~/classes/models/base.model";
import { DatabaseFieldType } from "./database_field_type.enum";

export class FirestoreClientDatabase extends DBAbstract {
  db: Firestore;

  constructor() {
    super();

    this.db = useFirestore();
  }

  private prepareMergeData(data: ModelDatabaseData) {
    const copiedData = { ...data };

    for (const key in copiedData) {
      if (copiedData[key] == undefined) {
        copiedData[key] = deleteField();
      }
    }

    return copiedData;
  }

  override generateId(databaseConfig: ModelDatabaseConfig): string {
    const collectinoReference = collection(this.db, databaseConfig.path);
    const docRef = doc(collectinoReference);
    return docRef.id;
  }

  override async get(databaseConfig: ModelDatabaseConfig) {
    const docRef = doc(this.db, databaseConfig.path);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.data()) {
      throw new DocumentNotFoundError(databaseConfig.path);
    }

    return {
      ...docSnapshot.data(),
      id: docSnapshot.id,
    } as ModelDatabaseData;
  }

  override async save(
    data: ModelDatabaseData,
    config: ModelDatabaseConfig,
    merge: boolean = true
  ): Promise<string> {
    try {
      if (data.id == undefined) {
        const docRef = collection(this.db, config.collection);

        delete data.id;
        const response = await addDoc(docRef, data);

        return response.id;
      } else {
        const docRef = doc(this.db, config.path);

        const copiedData = this.prepareMergeData(data);

        delete copiedData.id;

        await setDoc(docRef, copiedData, { merge: merge });

        return data.id;
      }
    } catch (error) {
      console.error(
        "Error saving data for path",
        data.id == undefined ? config.collection : config.path,
        error
      );

      throw error;
    }
  }

  override async delete(databaseConfig: ModelDatabaseConfig) {
    const docRef = doc(this.db, databaseConfig.path);
    await deleteDoc(docRef);
  }

  override async list(
    databaseConfig: ModelDatabaseConfig,
    queryData?: ModelQueryConfig
  ): Promise<ModelDatabaseData[]> {
    const collectionReference:
      | CollectionReference<DocumentData>
      | Query<DocumentData> = databaseConfig.collectionGroup
      ? collectionGroup(this.db, databaseConfig.collectionGroup)
      : collection(this.db, databaseConfig.path);

    const queryReference = this.buildQuery(collectionReference, queryData);

    const querySnapshot = await getDocs(queryReference ?? collectionReference);

    const data = querySnapshot.docs.map((doc) => {
      return {
        ...doc.data(),
        id: doc.id,
      } as ModelDatabaseData;
    });

    return data;
  }

  override streamRecord(
    databaseConfig: ModelDatabaseConfig
  ): Observable<ModelDatabaseData | undefined> {
    const docRef = doc(this.db, databaseConfig.path);

    return docData(docRef, { idField: "id" }).pipe(
      map((docSnapshot) => {
        if (!docSnapshot) {
          return undefined;
        }

        return docSnapshot as ModelDatabaseData;
      })
    );
  }

  override streamList(
    databaseConfig: ModelDatabaseConfig,
    queryData?: ModelQueryConfig | undefined
  ): Observable<ModelDatabaseData[]> {
    const collectionReference:
      | CollectionReference<DocumentData>
      | Query<DocumentData> = databaseConfig.collectionGroup
      ? collectionGroup(this.db, databaseConfig.collectionGroup)
      : collection(this.db, databaseConfig.path);

    const queryReference = this.buildQuery(collectionReference, queryData);

    return collectionData(queryReference ?? collectionReference, {
      idField: "id",
    }).pipe(
      map((docs: any) => docs.map((doc: any) => doc as ModelDatabaseData))
    );
  }

  private buildQuery(
    collectinoReference:
      | CollectionReference<DocumentData>
      | Query<DocumentData>,
    queryData?: ModelQueryConfig
  ) {
    let q = undefined;

    if (queryData) {
      for (const queryInstance of queryData) {
        switch (queryInstance.type) {
          case "where":
            if (
              queryInstance.field == undefined ||
              queryInstance.operator == undefined ||
              queryInstance.value == undefined
            )
              continue;

            let field: string | FieldPath = queryInstance.field;

            if (queryInstance.fieldType == DatabaseFieldType.id) {
              field = documentId();
            }

            q = query(
              q ?? collectinoReference,
              where(field, queryInstance.operator, queryInstance.value)
            );
            break;
          case "orderBy":
            if (
              queryInstance.field == undefined ||
              queryInstance.direction == undefined
            )
              continue;

            q = query(
              q ?? collectinoReference,
              orderBy(queryInstance.field!, queryInstance.direction!)
            );
            break;
          case "limit":
            if (queryInstance.value == undefined) continue;

            q = query(q ?? collectinoReference, limit(queryInstance.value));
            break;
        }
      }
    }

    return q ?? collectinoReference;
  }

  override async batchUpdate(models: BaseModel[]) {
    const batch = writeBatch(this.db);

    models.forEach((item) => {
      const data = item.toMap();

      if (data.id) {
        const docRef = doc(
          this.db,
          `${item.pathPrefixOverride ?? ""}` + item.databaseConfig.path
        );
        batch.set(docRef, this.prepareMergeData(data), { merge: true });
      } else {
        const docRef = doc(
          collection(
            this.db,
            `${item.pathPrefixOverride ?? ""}` + item.databaseConfig.collection
          )
        );
        batch.set(docRef, data);
      }
    });

    await batch.commit();
  }

  override async batchDelete(models: BaseModel[]) {
    const batch = writeBatch(this.db);

    models.forEach((item) => {
      const docRef = doc(this.db, item.databaseConfig.path);
      batch.delete(docRef);
    });

    await batch.commit();
  }
}
